import PropTypes from 'prop-types';
import React from 'react';

import withTheme from 'core/hocs/withTheme';
import Link from 'core/components/Link';
import modelPropTypes, {
  tagAttributes,
} from 'core/utils/prop-types/model';
import themePropTypes from 'core/utils/prop-types/theme';
import { resolveScopedStyles } from 'core/utils/styled-jsx';

/**
 * Обертка над Tags которая упрощает использование тегов при работе с данными из апи.
 *
 * @author Alexey Rezakov <a.rezakov@rambler-co.ru>
 * Переделал компонент @author Artyom Gavrilyuk <a.gavrilyuk@rambler-co.ru>
 */
function TopicTags(props, context) {
  const {
    data,
    hrefBuilder,
    disableTypes,
    theme: {
      animations: {
        hover: hoverAnimation,
      },
      controls: {
        topicFooter: {
          links: linksAtoms,
        },
        topicTags: tagsAtoms,
      },
    },
  } = props;

  if (!data) return null;

  const { mediaUrlPrefix } = context;

  /**
   * Исключаем отключенные теги.
   */
  const tags = data.filter(tag => (
    disableTypes.indexOf(tag.attributes.tag_type) === -1)
  );

  const scoped = resolveScopedStyles(
    <scope>
      <style jsx>{`
        .link
          border-bottom ${linksAtoms.borderBottom}
          transition border-color ${hoverAnimation}
          &:hover
            border-bottom ${linksAtoms.hover.borderBottom}
        .linkTitle
          font ${linksAtoms.title.font}
          color ${linksAtoms.title.color}
          margin-right ${linksAtoms.marginLeft}
        .linkWithHash
          font ${tagsAtoms.font}
          color ${tagsAtoms.color}
        .tag
          font ${linksAtoms.font}
          margin-right ${linksAtoms.marginLeft}
      `}</style>
      <style jsx>{`
        .tag
          display inline-flex
          &:last-child
            margin-right 0
      `}</style>
    </scope>
  );

  return (
    <React.Fragment>
      <span className={scoped.wrapClassNames('linkTitle')}>
        {tags.length === 1 ? 'Тег:' : 'Теги:'}
      </span>
      {tags.map((item, i) => (
        <div key={item.id} className={scoped.wrapClassNames('tag')}>
          <div className={scoped.wrapClassNames('linkWithHash')}>
            {tagsAtoms.showHash && '#'}
            <Link
              to={hrefBuilder(item.attributes.slug, item.attributes.tag_type, mediaUrlPrefix)}
              className={scoped.wrapClassNames('link')}
              type={linksAtoms.type}
            >
              {item.attributes.title}
            </Link>
          </div>
          {(linksAtoms.showComma && (i + 1) < tags.length) ? ',' : ''}
        </div>
      ))}
      <scoped.styles />
    </React.Fragment>
  );
}

TopicTags.propTypes = {
  /**
   * Функция для генерации ссылки на тег.<br/>
   * Принимает слаг тега и его тип.
   *
   *  @param {string} slug - tag slug
   *  @param {string} type - tag type
   *  @param {string} mediaUrlPrefix - media prefix
   */
  hrefBuilder: PropTypes.func,

  /**
   * Массив тегов.
   */
  data: PropTypes.arrayOf(modelPropTypes(tagAttributes).isRequired),

  /**
   * Типы тегов, которые не нужно выводить.
   */
  disableTypes: PropTypes.arrayOf(PropTypes.string),

  /**
   * @ignore
   */
  theme: themePropTypes(`{
    animations: {
      hover,
    },
    controls: {
      topicFooter: {
        links: {
          marginLeft,
          borderBottom,
          type,
          hover: {
            borderBottom,
          }
        },
      },
      topicTags: {
        font,
        color,
      }
    }
  }`),
};

TopicTags.contextTypes = {
  mediaUrlPrefix: PropTypes.string,
};

TopicTags.defaultProps = {
  disableTypes: [],
  hrefBuilder: (slug, tagType, prefix) => {
    const typeMap = {
      star: 'stars',
      common_tag: 'label',
    };

    const path = prefix || '';
    const type = typeMap[tagType] || tagType;

    return `${path}/${type}/${slug}`;
  },
};

const TopicTagsWithHOCs = withTheme(TopicTags);
TopicTagsWithHOCs.displayName = 'TopicTags';

export { TopicTags as StorybookComponent };
export default TopicTagsWithHOCs;
